/*
 * Standardized table styles for consistent appearance across the application
 * The border style defined here is the default for all tables, matching the beds page.
 */

export const tableStyles = (theme) => ({
  // Base table container styles
  tableContainer: {
    width: '100%',
    overflowX: 'auto',
    borderRadius: '4px',
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    '& .MuiTable-root': {
      minWidth: 650,
      borderCollapse: 'separate',
      borderSpacing: 0,
      '& caption': {
        captionSide: 'top',
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        padding: theme.spacing(1, 2),
        borderTopLeftRadius: '4px',
        borderTopRightRadius: '4px',
        fontSize: '1rem',
        fontWeight: 600,
      },
      // Ensure table cells have consistent border radius
      '& td:first-of-type, & th:first-of-type': {
        borderTopLeftRadius: '4px',
        borderBottomLeftRadius: '4px',
      },
      '& td:last-child, & th:last-child': {
        borderTopRightRadius: '4px',
        borderBottomRightRadius: '4px',
      },
    },
  },

  // Table header styles
  tableHead: {
    backgroundColor: theme.palette.primary.main,
    '& .MuiTableCell-head': {
      fontWeight: 700,
      fontSize: '0.875rem',
      color: theme.palette.secondary.main,
      backgroundColor: theme.palette.primary.main,
      borderBottom: `1px solid ${theme.palette.primary.dark}`,
      height: 40,
      minHeight: 40,
      maxHeight: 40,
      padding: '4px 16px',
      whiteSpace: 'nowrap',
      '&:not(:last-child)': {
        borderRight: `1px solid ${theme.palette.primary.dark}`,
      },
      '&:first-of-type': {
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderTopLeftRadius: '4px',
      },
      '&:last-of-type': {
        borderRight: `1px solid ${theme.palette.divider}`,
        borderTopRightRadius: '4px',
      },
    },
  },

  // Table body styles
  tableBody: {
    '& .MuiTableRow-root': {
      '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.background.default,
      },
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '&:last-child': {
        '& .MuiTableCell-root': {
          borderBottom: 'none',
          '&:first-of-type': {
            borderBottomLeftRadius: '4px',
          },
          '&:last-child': {
            borderBottomRightRadius: '4px',
          },
        },
      },
      '& .MuiTableCell-root': {
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1),
        '&:not(:last-child)': {
          borderRight: `1px solid ${theme.palette.divider}`,
        },
        '&:first-of-type': {
          borderLeft: `1px solid ${theme.palette.divider}`,
        },
        '&:last-child': {
          borderRight: `1px solid ${theme.palette.divider}`,
        },
      },
    },
  },

  // Cell styles
  tableCell: {
    padding: theme.spacing(0.5), // 4px
    fontSize: '0.875rem',
    '&.MuiTableCell-head': {
      fontWeight: 600,
    },
  },

  // Compact variant
  compact: {
    '& .MuiTableCell-root': {
      padding: theme.spacing(0.5, 1),
    },
  },

  // Borderless variant
  borderless: {
    border: 'none',
    '& .MuiTableCell-root': {
      border: 'none',
      '&:not(:last-child)': {
        borderRight: 'none',
      },
    },
  },

  // Hover effect
  hover: {
    '& .MuiTableRow-root:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
});

// Helper function to apply table styles
export const getTableStyles = (theme, variant = 'default') => {
  const styles = tableStyles(theme);
  const baseStyles = [styles.tableContainer];

  if (variant === 'compact') {
    baseStyles.push(styles.compact);
  } else if (variant === 'borderless') {
    baseStyles.push(styles.borderless);
  }

  return baseStyles;
};
